import React, { useState } from "react";
import { useFormState, useWatch } from "react-hook-form";

import {
  FormRow,
  SelectSheet,
  SelectSheetOption,
} from "../../../../components/MobileForm";
import {
  Card,
  CardLabel,
  RowsCard,
  HelperText,
} from "../../../../components/MobileForm/styles";
import { FormInput, InputDate } from "../../../../components/Input";
import {
  TRACKING_MODE_DESCRIPTIONS,
  TRACKING_MODE_IMMUTABLE_NOTE,
  TRACKING_MODE_LABELS,
} from "../../constants";
import { MobileCashSettlementField } from "./MobileCashSettlementField";
import { settlementAccountRule } from "./settlement";
import { MobileInvestmentFormProps } from "./types";

export const MobileInvestmentForm: React.FC<MobileInvestmentFormProps> = ({
  control,
  register,
  setValue,
  clearErrors,
  investmentTypes,
  trackingModeOptions,
  trackingMode,
  isEdit,
  isSubmitting,
  settlement,
}) => {
  const [isTypeSheetOpen, setIsTypeSheetOpen] = useState(false);
  const [isTrackingModeSheetOpen, setIsTrackingModeSheetOpen] = useState(false);

  const values = useWatch({ control });
  const { errors } = useFormState({ control });

  // The sheet-picked fields have no Controller input, so register them here only to attach validation.
  register("investmentType", { required: "Investment Type is required!" });
  if (!isEdit) {
    register("trackingMode", { required: "Tracking method is required!" });
  }
  register(
    "settlementAccount",
    settlementAccountRule(
      settlement.isShown &&
        settlement.isEditable &&
        settlement.value === "LINKED_ACCOUNT",
    ),
  );

  const handleTypeSelect = (option: SelectSheetOption) => {
    setValue("investmentType", option, {
      shouldDirty: true,
      shouldValidate: true,
    });
    clearErrors("investmentType");
    setIsTypeSheetOpen(false);
  };

  const handleTrackingModeSelect = (option: SelectSheetOption) => {
    const trackingMode = trackingModeOptions.find(
      (candidate) => candidate.value === option.value,
    );
    if (!trackingMode) {
      return;
    }
    setValue("trackingMode", trackingMode, {
      shouldDirty: true,
      shouldValidate: true,
    });
    clearErrors("trackingMode");
    setIsTrackingModeSheetOpen(false);
  };

  const handleSettlementAccountSelect = (option: SelectSheetOption) => {
    setValue("settlementAccount", option, {
      shouldDirty: true,
      shouldValidate: true,
    });
    clearErrors("settlementAccount");
  };

  return (
    <>
      <Card>
        <CardLabel htmlFor="mobile-investment-name">
          Investment name *
        </CardLabel>
        <FormInput
          id="mobile-investment-name"
          required
          name="name"
          control={control}
          rules={{
            required: "Name is required!",
            minLength: {
              value: 3,
              message: "Name should have more than 3 letters!",
            },
          }}
          handleClear={false}
          disabled={isSubmitting}
          placeholder="e.g. Trade Republic"
          autoFocus={true}
          variant="title"
        />
      </Card>

      <RowsCard>
        <FormRow
          label="Investment type"
          value={values.investmentType?.label}
          placeholder="Select type"
          error={errors.investmentType?.message}
          onClick={() => setIsTypeSheetOpen(true)}
        />
      </RowsCard>
      <HelperText>
        Drives how this holding is grouped in your allocation.
      </HelperText>

      {!isEdit && (
        <>
          <RowsCard>
            <FormRow
              label="Tracking method"
              value={values.trackingMode?.label}
              placeholder="Select method"
              error={errors.trackingMode?.message}
              onClick={() => setIsTrackingModeSheetOpen(true)}
            />
          </RowsCard>
          <HelperText>
            {TRACKING_MODE_DESCRIPTIONS.MANUAL_MARKET_UPDATES}{" "}
            {TRACKING_MODE_DESCRIPTIONS.HOLDINGS_LEDGER}{" "}
            {TRACKING_MODE_IMMUTABLE_NOTE}
          </HelperText>
        </>
      )}

      {isEdit && trackingMode && (
        <Card>
          <CardLabel>Tracking method</CardLabel>
          <strong>{TRACKING_MODE_LABELS[trackingMode]}</strong>
          <HelperText>{TRACKING_MODE_IMMUTABLE_NOTE}</HelperText>
        </Card>
      )}

      <MobileCashSettlementField
        settlement={settlement}
        isSubmitting={isSubmitting}
        error={errors.settlementAccount?.message}
        onPickAccount={handleSettlementAccountSelect}
      />

      <Card>
        <CardLabel htmlFor="mobile-investment-institution">
          Institution
        </CardLabel>
        <FormInput
          id="mobile-investment-institution"
          name="institution"
          control={control}
          handleClear={false}
          disabled={isSubmitting}
          placeholder="e.g. DeGiro"
        />
        <HelperText>
          Optional — the broker or platform holding this investment.
        </HelperText>

        <CardLabel htmlFor="mobile-investment-start-date">
          Start date *
        </CardLabel>
        <InputDate
          id="mobile-investment-start-date"
          name="startDate"
          control={control}
          rules={{ required: "Start date is required!" }}
          disabled={isSubmitting}
          maxDate={new Date()}
          placeholder="YYYY/MM/DD"
        />
        <HelperText>When you first funded this investment.</HelperText>
      </Card>

      <SelectSheet
        isOpen={isTypeSheetOpen}
        title="Select investment type"
        options={investmentTypes}
        selectedValue={values.investmentType?.value}
        onSelect={handleTypeSelect}
        onClose={() => setIsTypeSheetOpen(false)}
      />
      {!isEdit && (
        <SelectSheet
          isOpen={isTrackingModeSheetOpen}
          title="Select tracking method"
          options={trackingModeOptions}
          selectedValue={values.trackingMode?.value}
          onSelect={handleTrackingModeSelect}
          onClose={() => setIsTrackingModeSheetOpen(false)}
        />
      )}
    </>
  );
};
